import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ToastProvider } from "@/contexts/ToastContext";
import LoginPage from "@/pages/LoginPage";
import RecordPage from "@/pages/RecordPage";
import HistoryPage from "@/pages/HistoryPage";
import SummaryPage from "@/pages/SummaryPage";
import ProfilePage from "@/pages/ProfilePage";
import AppShell from "@/components/AppShell";
import PWAFeatures from "@/components/PWAFeatures";
import { logger } from "@/lib/logger";

function AuthCallback() {
  const navigate = useNavigate();
  const { processSessionId } = useAuth();
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const run = async () => {
      try {
        let sessionId = null;
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (match) sessionId = match[1];
        if (!sessionId) {
          const params = new URLSearchParams(window.location.search);
          sessionId = params.get("session_id");
        }

        if (sessionId) {
          await processSessionId(sessionId);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
      } catch (e) {
        logger.error("Auth callback error:", e);
        navigate("/login", { replace: true });
      }
    };

    run();
  }, [processSessionId, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900">
      <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full spin"></div>
      <p className="text-slate-400 mt-4 text-sm">Signing you in…</p>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full spin"></div>
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of session_id in hash BEFORE checkAuth runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/" element={<RecordPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/summary" element={<SummaryPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <PWAFeatures />
          <AppRouter />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
